'use client';
import React from 'react';
import { CheckCircle, Clock, AlertCircle, ChevronRight } from 'lucide-react';

interface Filing {
  id: string;
  type: string;
  period: string;
  dueDate: string;
  filedDate?: string;
  status: 'filed' | 'pending' | 'overdue';
  daysUntilDue?: number;
}

// Backend integration point: replace with API call to /api/taxpayer/:tin/filings
const filings: Filing[] = [
  { id: 'fil-001', type: 'GST Return', period: 'Q1 2026', dueDate: '04/30/2026', status: 'overdue' },
  { id: 'fil-002', type: 'Annual Income Tax', period: 'FY 2025', dueDate: '03/31/2026', status: 'overdue' },
  { id: 'fil-003', type: 'Withholding Tax', period: 'Apr 2026', dueDate: '05/15/2026', filedDate: '05/12/2026', status: 'filed' },
  { id: 'fil-004', type: 'GST Return', period: 'Q2 2026', dueDate: '07/31/2026', status: 'pending', daysUntilDue: 64 },
  { id: 'fil-005', type: 'Withholding Tax', period: 'May 2026', dueDate: '06/15/2026', status: 'pending', daysUntilDue: 18 },
];

const statusIcon = {
  filed: <CheckCircle size={14} style={{ color: '#4ade80' }} />,
  pending: <Clock size={14} style={{ color: 'var(--accent)' }} />,
  overdue: <AlertCircle size={14} style={{ color: '#f87171' }} />,
};

const statusColor = {
  filed: '#4ade80',
  pending: 'var(--accent)',
  overdue: '#f87171',
};

interface FilingStatusTrackerProps {
  compact?: boolean;
}

export default function FilingStatusTracker({ compact = false }: FilingStatusTrackerProps) {
  const display = compact ? filings.slice(0, 4) : filings;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
          Filing Status
        </h3>
        {compact && (
          <button className="text-xs flex items-center gap-0.5" style={{ color: 'var(--accent)' }}>
            All filings <ChevronRight size={11} />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>
          <span>Filing compliance</span>
          <span className="font-semibold tabular-nums" style={{ color: 'var(--accent)' }}>
            {Math.round((filings.filter((f) => f.status === 'filed').length / filings.length) * 100)}%
          </span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${(filings.filter((f) => f.status === 'filed').length / filings.length) * 100}%`,
              background: 'linear-gradient(90deg, #b8860b, #f0c040)',
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {display.map((filing) => (
          <div
            key={filing.id}
            className="flex items-center gap-3 py-2 px-3 rounded-lg transition-all hover:brightness-110"
            style={{
              backgroundColor:
                filing.status === 'overdue' ?'rgba(220,38,38,0.08)'
                  : filing.status === 'filed' ?'rgba(74,222,128,0.05)' :'rgba(255,255,255,0.03)',
              border: `1px solid ${
                filing.status === 'overdue' ?'rgba(220,38,38,0.2)'
                  : filing.status === 'filed' ?'rgba(74,222,128,0.15)' :'var(--dark-border)'
              }`,
            }}
          >
            <div className="flex-shrink-0">{statusIcon[filing.status]}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>
                {filing.type}
              </p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                {filing.period} · Due: {filing.dueDate}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              {filing.status === 'filed' && filing.filedDate && (
                <p className="text-xs" style={{ color: '#4ade80' }}>
                  Filed {filing.filedDate}
                </p>
              )}
              {filing.status === 'overdue' && (
                <p className="text-xs font-semibold" style={{ color: '#f87171' }}>
                  OVERDUE
                </p>
              )}
              {filing.status === 'pending' && filing.daysUntilDue && (
                <p className="text-xs" style={{ color: 'var(--accent)' }}>
                  {filing.daysUntilDue}d left
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {!compact && (
        <button
          className="mt-4 w-full py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90 active:scale-95"
          style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
        >
          File a Return Online
        </button>
      )}
    </div>
  );
}